/**
 * @file 紙面の一部を切り取って、その場で見せるための絵にする。
 *
 * @what: 画像の URL と割合 (0〜1) の範囲を受け、その範囲だけの PNG の URL を作る。
 * @why: 囲んだところを大きく見るのに、サーバへ問い合わせる必要はない。手元の canvas で足りる。
 * @failure: 画像が読めない・canvas が使えないときは throw する (呼び出し側が文言を出す)。
 */

export async function cropImageToUrl(
  imageUrl: string,
  rect: { x: number; y: number; w: number; h: number },
): Promise<string> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("failed to load the page image"));
    img.src = imageUrl;
  });
  const sx = Math.max(0, Math.round(img.naturalWidth * rect.x));
  const sy = Math.max(0, Math.round(img.naturalHeight * rect.y));
  const sw = Math.min(img.naturalWidth - sx, Math.round(img.naturalWidth * rect.w));
  const sh = Math.min(img.naturalHeight - sy, Math.round(img.naturalHeight * rect.h));
  if (sw <= 0 || sh <= 0) throw new Error("the selected area is empty");
  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas is unavailable");
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("failed to make an image");
  return URL.createObjectURL(blob);
}
